import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Gavel, Search, Loader2, ExternalLink, Sparkles, MessageSquareQuote, Bookmark, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

export default function CaseLaw() {
  const [searchParams] = useSearchParams();
  const courseSlug = searchParams.get("course") || null;
  const [mode, setMode] = useState("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedUrls, setSavedUrls] = useState(new Set());

  const saveCase = async (r) => {
    try {
      await api.post("/bookmarks", { kind: "case", title: r.case_name, citation: r.citation, court: r.court, date_filed: r.date_filed, url: r.url, course_slug: courseSlug });
      setSavedUrls((s) => new Set(s).add(r.url));
      toast.success(courseSlug ? "Saved to course research" : "Saved to research");
    } catch { toast.error("Could not save"); }
  };

  const saveAnswer = async () => {
    try {
      await api.post("/bookmarks", { kind: "answer", title: `Q: ${query.slice(0, 80)}`, content: answer, course_slug: courseSlug });
      toast.success("Answer saved to research");
    } catch { toast.error("Could not save"); }
  };

  const runWith = async (q, m) => {
    if (!q.trim() || loading) return;
    setLoading(true);
    setError("");
    setResults(null);
    setAnswer(null);
    setSources([]);
    try {
      if (m === "search") {
        const { data } = await api.post("/caselaw/search", { query: q, limit: 10 });
        if (data.error === "rate_limited") setError("The case-law source is temporarily rate-limited. Try again shortly.");
        setResults(data.results || []);
      } else {
        const { data } = await api.post("/caselaw/ask", { question: q, limit: 4 });
        setAnswer(data.answer);
        setSources(data.sources || []);
      }
    } catch {
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      runWith(q, "search");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = () => runWith(query, mode);

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <Gavel className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">Case Law Search</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Search millions of open U.S. court opinions (via CourtListener / the Free Law Project), or ask a question and
          let the AI read the opinions and answer with citations. Research assistance, not legal advice.
        </p>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode("search")} data-testid="caselaw-mode-search"
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono border transition-colors ${mode === "search" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
            <Search className="w-4 h-4" /> Search opinions
          </button>
          <button onClick={() => setMode("ask")} data-testid="caselaw-mode-ask"
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono border transition-colors ${mode === "ask" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
            <Sparkles className="w-4 h-4" /> Ask the AI
          </button>
        </div>

        <div className="flex gap-3 mb-10">
          <input
            data-testid="caselaw-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder={mode === "search" ? "e.g. Saenz v. Roe, or 'implied warranty of habitability'" : "e.g. Does the FDCPA apply to attorneys?"}
            className="flex-1 bg-muted border border-input px-4 py-3 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          />
          <button onClick={run} disabled={loading} data-testid="caselaw-submit"
            className="bg-primary text-primary-foreground px-6 rounded-sm flex items-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "search" ? <Search className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
            {mode === "search" ? "Search" : "Ask"}
          </button>
        </div>

        {error && <div className="mb-6 text-sm text-destructive-foreground bg-destructive/30 border border-destructive px-4 py-3">{error}</div>}

        {loading && (
          <div className="text-center py-16 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            {mode === "ask" ? "Reading opinions and drafting an answer…" : "Searching court opinions…"}
          </div>
        )}

        {/* AI answer */}
        {!loading && answer && (
          <div className="space-y-6">
            <div className="bg-card border border-border p-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-primary">
                  <MessageSquareQuote className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-widest font-mono">AI Answer</span>
                </div>
                <button onClick={saveAnswer} data-testid="caselaw-save-answer" className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-muted-foreground hover:text-primary transition-colors">
                  <Bookmark className="w-4 h-4" /> Save answer
                </button>
              </div>
              <div className="prose-chat text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown></div>
            </div>
            {sources.length > 0 && (
              <div>
                <h3 className="font-serif text-2xl mb-3">Sources</h3>
                <div className="space-y-2">
                  {sources.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`caselaw-source-${i}`}
                      className="flex items-center justify-between bg-card border border-border p-4 hover:border-primary transition-colors group">
                      <div>
                        <div className="font-serif text-lg group-hover:text-primary transition-colors">[{i + 1}] {s.case_name}</div>
                        <div className="text-xs font-mono text-muted-foreground">{s.citation || s.court} {s.date_filed && `· ${s.date_filed.slice(0,10)}`}</div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search results */}
        {!loading && results && (
          <div className="space-y-3">
            {results.length === 0 && !error && <p className="text-muted-foreground">No opinions found. Try a different query.</p>}
            {results.map((r, i) => (
              <div key={i} data-testid={`caselaw-result-${i}`} className="block bg-card border border-border p-5 hover:border-primary transition-colors group">
                <div className="flex items-start justify-between gap-4">
                  <a href={r.url} target="_blank" rel="noreferrer" className="flex-1">
                    <div className="font-serif text-xl group-hover:text-primary transition-colors leading-tight">{r.case_name}</div>
                    <div className="text-xs font-mono text-muted-foreground mt-1">{r.citation || r.court} {r.date_filed && `· ${r.date_filed.slice(0,10)}`}</div>
                  </a>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => saveCase(r)} data-testid={`caselaw-save-${i}`} title="Save to research"
                      className={`transition-colors ${savedUrls.has(r.url) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {savedUrls.has(r.url) ? <Check className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-muted-foreground group-hover:text-primary"><ExternalLink className="w-4 h-4" /></a>
                  </div>
                </div>
                {r.snippet && <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3">{r.snippet}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
